import {
  forwardRef,
  useState,
  type ReactNode,
  type RefObject,
  type MouseEvent,
  type ForwardedRef,
} from 'react';
import { useTransition, animated, config } from '@react-spring/web';
import { useGesture } from '@use-gesture/react';
import { useMeasure } from 'react-use';
import { X } from '@hungryroot/icons';
import { ModalContent, ModalOverlay } from '@hungryroot/ramen';

import styles from './Sheet.module.css';

const AnimatedDialogOverlay = animated(ModalOverlay);
const AnimatedDialogContent = animated(ModalContent);

function getHeightViewModel(
  params: Pick<Props, 'height' | 'contentWrapperClassName'>
): {
  containerClassName: string;
  contentWrapperClassName: string;
  bodyClassName: string;
  bounce?: 0;
} {
  const {
    height: variant,
    contentWrapperClassName: contentWrapperClassNameProp,
  } = params;
  switch (variant) {
    case 'full':
      return {
        containerClassName: styles['container-full-screen'],
        contentWrapperClassName: styles['content-wrapper-full-screen'],
        bodyClassName: `${styles['body-full-screen']} ${contentWrapperClassNameProp}`,
        bounce: 0,
      };
    case 'auto':
    case undefined:
      return {
        containerClassName: styles.container,
        contentWrapperClassName: styles['content-wrapper'],
        bodyClassName: `${styles.body} ${contentWrapperClassNameProp}`,
      };
  }
}

type Props = {
  active: boolean;
  testid?: string;
  children: ReactNode;
  ariaLabel: string;
  initialFocusRef:
    | RefObject<HTMLButtonElement>
    | RefObject<HTMLInputElement>
    | undefined;
  onRequestDismiss: () => void;
  onMouseDown?: (e: MouseEvent) => void;
  height?: 'auto' | 'full';
  closeButton?: null;
  touchBar?: null;
  contentWrapperClassName?: string;
  className?: string;
  contentClassName?: string;
  contentBodyClassName?: string;
  swipeCloseSegmentEvent?: () => void;
  showXButton?: boolean;
  touchBarClassName?: string;
};

/**
 * @deprecated Use `Sheet` from Ramen instead.
 */
export const Sheet = forwardRef(
  (props: Props, forwardedRef: ForwardedRef<HTMLDivElement>) => {
    const {
      active,
      ariaLabel,
      onRequestDismiss,
      initialFocusRef,
      testid,
      onMouseDown,
      closeButton,
      touchBar,
      className,
      contentClassName,
      contentBodyClassName,
      swipeCloseSegmentEvent,
      showXButton = true,
      touchBarClassName,
    } = props;
    const [ref, { height }] = useMeasure<HTMLDivElement>();
    const [dragY, setDragY] = useState(0);

    const {
      containerClassName,
      bodyClassName,
      contentWrapperClassName,
      bounce,
    } = getHeightViewModel(props);

    const dragPct = height === 0 ? 0 : dragY / height;
    const transitions = useTransition(active, {
      config: {
        ...config.stiff,
        bounce,
      },
      from: {
        transform: `translate3d(0, 100%, 0)`,
        opacity: 0,
      },
      update: {
        transform: `translate3d(0, ${dragPct * 100}%, 0)`,
        opacity: 1,
      },
      enter: {
        transform: `translate3d(0, 0%, 0)`,
        opacity: 1,
      },
      leave: {
        transform: `translate3d(0, 100%, 0)`,
        opacity: 0,
      },
    });

    const drag = useGesture({
      onDrag(state) {
        setDragY(state.xy[1] - state.initial[1]);
      },
      onDragEnd(state) {
        const { direction } = state;
        if (direction[1] === 1) {
          onRequestDismiss();

          if (swipeCloseSegmentEvent) {
            swipeCloseSegmentEvent();
          }
        }
        setDragY(0);
      },
    });

    return transitions(({ transform, opacity }, item) => {
      if (item === false) {
        return null;
      }

      const spring = { transform };

      return (
        <AnimatedDialogOverlay
          ref={forwardedRef}
          onDismiss={onRequestDismiss}
          data-testid={testid}
          style={{ opacity }}
          className={`${containerClassName} ${className}`}
          isOpen={item}
          initialFocusRef={initialFocusRef}
        >
          <AnimatedDialogContent
            ariaLabel={ariaLabel}
            data-testid={`${testid}-body`}
            style={spring}
            ref={ref}
            className={`${contentWrapperClassName} ${
              contentClassName === undefined ? '' : contentClassName
            }`}
            onMouseDown={(e) => {
              onMouseDown ? onMouseDown(e) : null;
            }}
          >
            <div
              data-testid={`${testid}-body-wrapper`}
              className={`${bodyClassName} ${contentBodyClassName}`}
            >
              {touchBar === undefined ? (
                <span
                  data-testid={`${testid}-touch-bar`}
                  className={
                    touchBarClassName ? touchBarClassName : styles['touch-bar']
                  }
                  {...drag()}
                ></span>
              ) : (
                touchBar
              )}
              {closeButton === undefined && showXButton ? (
                <button
                  onClick={onRequestDismiss}
                  data-testid={`${testid}-close-button`}
                  className={styles['close-button']}
                >
                  <X />
                </button>
              ) : (
                closeButton
              )}
              {props.children}
            </div>
          </AnimatedDialogContent>
        </AnimatedDialogOverlay>
      );
    });
  }
);
